import { useEffect, useRef, useState } from 'react';
import { ArrowRight, /* Users, */ Award, Check, Star, Play } from 'lucide-react';
import { videos, testimonios, nivelesEmbajador } from '@/data/datos';
import type { Producto, Embajador } from '@/types';

type Page = 'inicio' | 'embajador' | 'catalogo' | 'tratamientos';

interface EmbajadorProps {
  onNavigate: (page: Page, params?: { linea?: string; producto?: Producto }) => void;
  ambassador: Embajador | null;
}

export default function Embajador({ onNavigate, ambassador }: EmbajadorProps) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const lastX = useRef(0);
  const velocity = useRef(0);
  const touchDirection = useRef<'horizontal' | 'vertical' | null>(null);
  const scrollLeftStart = useRef(0);
  const scrollPos = useRef(0);

  // Helper to get YouTube ID from URL
  const getYoutubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return (match && match[2].length === 11) ? match[2] : 'yWQqtKdHnG4';
  };

  const videoId = getYoutubeId(ambassador?.embajador_video_url || videos.embajador);
  const displayName = ambassador?.showLeaderVideoInfoEmbajador ? ambassador.leaderName : ambassador?.nombre_completo;
  const displayRank = ambassador?.showLeaderVideoInfoEmbajador ? ambassador.leaderRank : ambassador?.rango;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!marquee || !marquee.firstElementChild) return;

    const innerContent = marquee.firstElementChild as HTMLElement;
    let animationFrameId: number;
    const speed = 0.5; //Velocidad del carrusel de Testimonios

    const animate = () => {
      if (!isDragging.current) {
        // Si hay velocidad (inercia), mover según la velocidad y aplicar fricción
        if (Math.abs(velocity.current) > 0.1) {
          scrollPos.current += velocity.current;
          velocity.current *= 0.95; // Fricción (frenado gradual)
        } else {
          // Si no hay inercia y no está en pausa, auto-scroll normal
          if (!isPaused) {
            scrollPos.current += speed;
          }
          velocity.current = 0;
        }
        
        const halfWidth = innerContent.scrollWidth / 2;
        if (scrollPos.current >= halfWidth) scrollPos.current -= halfWidth;
        if (scrollPos.current < 0) scrollPos.current += halfWidth;
        
        innerContent.style.transform = `translate3d(-${scrollPos.current}px, 0, 0)`;
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isPaused]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!marqueeRef.current || !marqueeRef.current.firstElementChild) return;
    isDragging.current = true;
    setIsPaused(true);
    startX.current = e.pageX;
    lastX.current = e.pageX;
    velocity.current = 0;
    scrollLeftStart.current = scrollPos.current;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !marqueeRef.current || !marqueeRef.current.firstElementChild) return;
    e.preventDefault();
    const x = e.pageX;
    
    // Calcular velocidad instantánea
    velocity.current = (lastX.current - x);
    lastX.current = x;

    const walk = (x - startX.current) * 1.2;
    let newPos = scrollLeftStart.current - walk;
    
    const innerContent = marqueeRef.current.firstElementChild as HTMLElement;
    const halfWidth = innerContent.scrollWidth / 2;
    if (newPos < 0) newPos += halfWidth;
    if (newPos >= halfWidth) newPos -= halfWidth;
    
    scrollPos.current = newPos;
    innerContent.style.transform = `translate3d(-${newPos}px, 0, 0)`;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    setIsPaused(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!marqueeRef.current || !marqueeRef.current.firstElementChild) return;
    isDragging.current = true;
    setIsPaused(true);
    startX.current = e.touches[0].pageX;
    startY.current = e.touches[0].pageY;
    lastX.current = e.touches[0].pageX;
    velocity.current = 0;
    touchDirection.current = null;
    scrollLeftStart.current = scrollPos.current;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !marqueeRef.current || !marqueeRef.current.firstElementChild) return;
    
    const x = e.touches[0].pageX;
    const y = e.touches[0].pageY;
    const diffX = x - startX.current;
    const diffY = y - startY.current;

    // Detectar dirección del movimiento inicial
    if (touchDirection.current === null) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        touchDirection.current = 'horizontal';
      } else if (Math.abs(diffY) > 5) {
        touchDirection.current = 'vertical';
      }
    }

    // Si el movimiento es vertical, no hacemos nada y dejamos que la página haga scroll
    if (touchDirection.current === 'vertical') {
      isDragging.current = false;
      setIsPaused(false);
      return;
    }

    // Si el movimiento es horizontal, movemos el carrusel y prevenimos el scroll de la página
    if (touchDirection.current === 'horizontal') {
      if (e.cancelable) e.preventDefault();
      
      // Calcular velocidad instantánea para el lanzamiento
      velocity.current = (lastX.current - x);
      lastX.current = x;

      const walk = diffX * 1.2;
      let newPos = scrollLeftStart.current - walk;
      
      const innerContent = marqueeRef.current.firstElementChild as HTMLElement;
      const halfWidth = innerContent.scrollWidth / 2;
      
      if (newPos < 0) newPos += halfWidth;
      if (newPos >= halfWidth) newPos -= halfWidth;
      
      scrollPos.current = newPos;
      innerContent.style.transform = `translate3d(-${newPos}px, 0, 0)`;
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    setIsPaused(false);
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      isDragging.current = false;
    }
    setIsPaused(false);
  };

  return (
    <div ref={sectionRef} className="min-h-screen bg-[#f8f9f5]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 pt-[96px]">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="space-y-8 reveal opacity-0">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#e91e8c]/10 rounded-full">
                  {/* <Users className="w-4 h-4 text-[#e91e8c]" /> */}
                  <span className="text-sm font-medium text-[#e91e8c]">Programa de Embajadores</span>
                </div>

                <h1 className="font-serif text-5xl md:text-6xl text-[#8abd31] leading-tight">
                  Transforma vidas,{' '}
                  <span className="text-[#e91e8c]">construye tu legado</span>
                </h1>

                <p className="text-lg text-gray-600 leading-relaxed">
                  Únete a la red de embajadores Ismerely y descubre un modelo de negocio 
                  probado que te permite generar ingresos mientras ayudas a otros a mejorar su salud.
                </p>

                <div className="flex flex-wrap gap-4">
                  <button 
                    onClick={() => window.dispatchEvent(new CustomEvent('open-chatbot'))}
                    className="btn-accent flex items-center gap-2 group"
                  >
                    <span>Registrarme</span>
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </button>
                  <button 
                    onClick={() => document.getElementById('niveles')?.scrollIntoView({ behavior: 'smooth' })}
                    className="px-6 py-3 border-2 border-[#8abd31] text-[#8abd31] rounded-full font-medium transition-all duration-300 hover:bg-[#8abd31] hover:text-white"
                  >
                    Plan de rangos
                  </button>
                </div>

                {/* Benefits */}
                <div className="space-y-3 pt-4">
                  {[
                    'Baja inversión, alta rentabilidad',
                    'Cero riesgos de perder tu inversión',
                    'Acompañamiento constante',
                    'Productos con alta demanda'
                  ].map((benefit) => (
                    <div key={benefit} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-[#8abd31] rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Video */}
              <div className="relative reveal opacity-0" style={{ animationDelay: '200ms' }}>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#8abd31]/20">
                  <div className="aspect-video relative">
                    {!isVideoPlaying ? (
                      <div 
                        className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                        onClick={() => setIsVideoPlaying(true)}
                      >
                        <img 
                          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
                          alt={`Video Thumbnail - ${displayName}`}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                        <div className="relative z-10 w-[75px] h-[75px] md:w-20 md:h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                          <Play className="w-[27px] h-[27px] md:w-8 md:h-8 text-[#e91e8c] ml-1" />
                        </div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="text-white font-medium">{displayName}</div>
                          <div className="text-white/70 text-sm">{displayRank}</div>
                        </div>
                      </div>
                    ) : (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                        title="Plan de compensación Ismerely"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                </div>
                
                {/* Decorative */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#e91e8c]/10 rounded-full blur-xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#8abd31]/10 rounded-full blur-xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Niveles Section */}
      <section id="niveles" className="py-24 bg-white">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 reveal opacity-0">
              <h2 className="font-serif text-4xl md:text-5xl text-[#e91e8c] mb-4">
                Plan de Rangos
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Escalan en tu carrera Ismerely y desbloquea mayores beneficios en cada nivel.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nivelesEmbajador.map((nivel, index) => (
                <div
                  key={nivel.nombre}
                  className="group relative p-8 rounded-2xl bg-[#f8f9f5] hover:bg-white hover:shadow-xl transition-all duration-500 reveal opacity-0"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div 
                    className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
                    style={{ backgroundColor: nivel.color }}
                  />
                  
                  <div className="mb-6">
                    <Award className="w-10 h-10" style={{ color: nivel.color }} />
                  </div>

                  <h3 className="font-serif text-2xl text-[#8abd31] mb-2">{`${index + 1}. ${nivel.nombre}`}</h3>
                  <div className="mb-4">
                    <ul className="list-disc list-inside text-sm text-gray-500">
                      {nivel.requisitos.map((requisito) => (
                        <li key={requisito}>{requisito}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-[#8abd31] mb-2">Beneficios:</p>
                    <ul className="space-y-2">
                      {nivel.beneficios.map((beneficio) => (
                        <li key={beneficio} className="flex items-start gap-2 text-sm text-gray-600">
                          <Check className="w-4 h-4 text-[#8abd31] flex-shrink-0 mt-0.5" />
                          <span>{beneficio}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Plan de Compensación Section
      <section id="plan" className="py-24 bg-gradient-to-br from-[#8abd31] to-[#8abd31]">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              * Left Content *
              <div className="space-y-8 reveal opacity-0">
                <div>
                  <h2 className="font-serif text-4xl md:text-5xl text-white mb-4">
                    Plan de Compensación
                  </h2>
                  <p className="text-white/70 text-lg">
                    7 formas de ganar con Ismerely. Un modelo de negocio justo y generoso.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { icon: DollarSign, title: 'Ganancia Directa', desc: 'Margen en cada venta' },
                    { icon: Users, title: 'Bonificación por Equipo', desc: 'Hasta 5 niveles' },
                    { icon: TrendingUp, title: 'Bono de Rango', desc: 'Hasta $20,000 mensuales' },
                    { icon: Award, title: 'Viajes e Incentivos', desc: 'Convenciones y autos' }
                  ].map((item) => (
                    <div 
                      key={item.title}
                      className="p-4 bg-white/10 rounded-xl backdrop-blur-sm"
                    >
                      <item.icon className="w-8 h-8 text-[#8abd31] mb-3" />
                      <h4 className="font-medium text-white mb-1">{item.title}</h4>
                      <p className="text-sm text-white/60">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              * Right Info Card *
              <div className="bg-white rounded-2xl p-8 reveal opacity-0" style={{ animationDelay: '200ms' }}>
                <h3 className="font-serif text-2xl text-[#8abd31] mb-6">
                  Detalles del Plan
                </h3>
                <div className="prose prose-sm max-h-[400px] overflow-y-auto pr-4 scrollbar-hide">
                  <pre className="whitespace-pre-wrap font-sans text-gray-600 text-sm leading-relaxed">
                    {informacionPlanCompensacion}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Testimonios Section */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 reveal opacity-0">
              <h2 className="font-serif text-4xl md:text-5xl text-[#8abd31] mb-4">
                Historias de Éxito
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Conoce las experiencias de quienes ya están transformando vidas con Ismerely.
              </p>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative">
          <div 
            ref={marqueeRef}
            className="flex overflow-x-hidden cursor-grab active:cursor-grabbing select-none"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={() => setIsPaused(true)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="flex w-max">
              {/* First set of testimonials */}
              <div className="flex items-stretch">
                {testimonios.map((testimonio) => (
                  <div
                    key={`${testimonio.id}-first`}
                    className="flex-shrink-0 w-[400px] mx-4"
                  >
                    <div className="p-8 rounded-2xl bg-[#f8f9f5] border border-gray-100 h-full">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#e91e8c] text-[#e91e8c]" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed">
                        &ldquo;{testimonio.texto}&rdquo;
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#8abd31] rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">
                            {testimonio.nombre.charAt(0)}
                          </span>
                        </div>
                        <div>
                          {testimonio.contacto ? (
                            <a 
                              href={testimonio.contacto}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-[#8abd31] hover:text-[#e91e8c] transition-colors"
                            >
                              {testimonio.nombre}
                            </a>
                          ) : (
                            <div className="font-medium text-[#8abd31]">{testimonio.nombre}</div>
                          )}
                          <div className="text-sm text-gray-500">{testimonio.rango || 'Líder Networker Bronce'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Second set of testimonials (identical for seamless loop) */}
              <div className="flex items-stretch">
                {testimonios.map((testimonio) => (
                  <div
                    key={`${testimonio.id}-second`}
                    className="flex-shrink-0 w-[400px] mx-4"
                  >
                    <div className="p-8 rounded-2xl bg-[#f8f9f5] border border-gray-100 h-full">
                      <div className="flex items-center gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#e91e8c] text-[#e91e8c]" />
                        ))}
                      </div>
                      <p className="text-gray-700 mb-6 leading-relaxed">
                        &ldquo;{testimonio.texto}&rdquo;
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#8abd31] rounded-full flex items-center justify-center">
                          <span className="text-white font-medium">
                            {testimonio.nombre.charAt(0)}
                          </span>
                        </div>
                        <div>
                          {testimonio.contacto ? (
                            <a 
                              href={testimonio.contacto}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-[#8abd31] hover:text-[#e91e8c] transition-colors"
                            >
                              {testimonio.nombre}
                            </a>
                          ) : (
                            <div className="font-medium text-[#8abd31]">{testimonio.nombre}</div>
                          )}
                          <div className="text-sm text-gray-500">{testimonio.rango || 'Líder Networker Bronce'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#f8f9f5]">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto text-center reveal opacity-0">
            <h2 className="font-serif text-4xl md:text-5xl text-[#8abd31] mb-6">
              ¿Listo para comenzar?
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              Habla con nuestro asistente virtual para iniciar tu registro como embajador Ismerely.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => onNavigate('catalogo')}
                className="btn-primary flex items-center gap-2"
              >
                <span>Explorar productos</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => onNavigate('tratamientos')}
                className="px-6 py-3 border-2 border-[#8abd31] text-[#8abd31] rounded-full font-medium transition-all duration-300 hover:bg-[#8abd31] hover:text-white"
              >
                Ver tratamientos
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
